/**
 * Grafik — „Kiedy pracuję" (T-B2): the week pattern on the left, „Inaczej niż zwykle" on the right,
 * and the dialogs they open (T-B2e in place, T-B2w, T-B2b, T-B2c).
 *
 * One read (`useScheduleRead`) behind one gate; while it waits the week card holds its shape as
 * bars and the deviations card is not drawn — its height is its list's, which only the answer knows.
 * The zone the hours are stated on stands in the header row's right end — read-only, the trainer's
 * own stored zone — through the slot the header gives a tab. The week's edits are staged until
 * „Zapisz" (`useWeekDraft`); the exceptions and blocks are written as each dialog is saved
 * (`useDeviationWrites`). The copy of Monday onto the week asks first (T-B2c), in mobile's words,
 * and so does undoing a line of the list.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { ConfirmDialog, type ConfirmPrompt } from '~/shared/ui/ConfirmDialog';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useDeviationWrites } from '../lib/useDeviationWrites';
import { useScheduleRead } from '../lib/useScheduleRead';
import { useWeekDraft } from '../lib/useWeekDraft';
import { zoneName } from '../model/calendarWords';
import { deviationMarks, deviationMonth, upcomingDeviations } from '../model/scheduleDeviations';
import { dayCopied, mondayCopiedToWeek, type Weekday } from '../model/weekPattern';
import { BlockDialog } from './BlockDialog';
import { CopyDayDialog } from './CopyDayDialog';
import { DeviationsCard } from './DeviationsCard';
import { ExceptionDialog } from './ExceptionDialog';
import { WeekCard } from './WeekCard';
import frame from './bookingTab.module.css';
import styles from './ScheduleTab.module.css';

interface ScheduleTabProps {
  /** The header row's right end, where the zone stands while this tab is shown. */
  readonly headerActions: HTMLElement | null;
}

export function ScheduleTab({ headerActions }: ScheduleTabProps) {
  const { t, i18n } = useTranslation();
  const announcer = useAnnouncer();
  const read = useScheduleRead();
  const week = useWeekDraft(read, announcer.announce);
  const writes = useDeviationWrites(read, announcer.announce);
  const [openDay, setOpenDay] = useState<Weekday | null>(null);
  const [copyFrom, setCopyFrom] = useState<Weekday | null>(null);
  const [askingMonday, setAskingMonday] = useState(false);
  const { gate } = read;

  const mondayEmpty = week.pattern.days.find((day) => day.weekday === 1)?.windows.length === 0;
  const mondayPrompt: ConfirmPrompt = mondayEmpty
    ? {
        title: t('web:booking.trainer.schedule.copyMondayEraseTitle'),
        description: t('web:booking.trainer.schedule.copyMondayEraseBody'),
        confirmLabel: t('web:booking.trainer.schedule.copyMondayEraseConfirm'),
        cancelLabel: t('web:booking.schedule.cancel'),
        destructiveConfirm: true,
      }
    : {
        title: t('web:booking.schedule.copyMondayTitle'),
        description: t('web:booking.trainer.schedule.copyMondayBody'),
        confirmLabel: t('web:booking.trainer.schedule.copyMondayConfirm'),
        cancelLabel: t('web:booking.schedule.cancel'),
      };
  const prompt = week.leavePrompt ?? (askingMonday ? mondayPrompt : writes.prompt);

  const days = deviationMonth(read.today);
  const availability = read.availability;

  return (
    <div className={frame.scroller}>
      {headerActions === null
        ? null
        : createPortal(
            // Drawn once the read has answered, and only as the trainer's zone when one is stored:
            // without it the hours stand on the server's default clock, and no client is offered a
            // slot (R3) — so the slot says the zone is not set rather than naming the default.
            gate.waiting ? null : (
              <span className={styles.zone}>
                {read.publishedZone === null
                  ? t('web:booking.schedule.zoneUnset')
                  : t('web:booking.schedule.zone', {
                      zone: zoneName(read.publishedZone, i18n.language, read.nowMs),
                    })}
              </span>
            ),
            headerActions,
          )}
      <p className={frame.intro}>{t('web:booking.schedule.intro')}</p>
      {!gate.waiting && read.availability !== undefined && read.publishedZone === null ? (
        <p className={frame.intro}>{t('web:booking.schedule.zoneUnsetLine')}</p>
      ) : null}

      <div className={frame.layout} aria-busy={gate.waiting}>
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:booking.schedule.loading')}</span>
            <div className={styles.weekColumn}>
              <WeekCard
                draft={week}
                openDay={null}
                onOpenDay={setOpenDay}
                onCopyDay={setCopyFrom}
                onCopyMonday={() => {
                  setAskingMonday(true);
                }}
                waiting={{ bars: gate.skeleton }}
              />
            </div>
          </>
        ) : read.failed || availability === undefined ? (
          <div
            className={`${String(frame.card)} ${String(frame.failed)} ${gate.reveal}`}
            role="alert"
          >
            <p className={frame.cardTitle}>{t('web:booking.schedule.unreadTitle')}</p>
            <button type="button" className={styles.tinyGhost} onClick={read.retry}>
              {t('web:booking.schedule.retry')}
            </button>
          </div>
        ) : (
          <>
            <div className={`${String(styles.weekColumn)} ${gate.reveal}`}>
              <WeekCard
                draft={week}
                openDay={openDay}
                onOpenDay={setOpenDay}
                onCopyDay={setCopyFrom}
                onCopyMonday={() => {
                  setAskingMonday(true);
                }}
              />
            </div>
            <div className={`${String(styles.deviationsColumn)} ${gate.reveal}`}>
              <DeviationsCard
                days={days}
                marks={deviationMarks(availability, days, read.zone)}
                deviations={upcomingDeviations(availability, {
                  today: read.today,
                  nowMs: read.nowMs,
                  zone: read.zone,
                })}
                today={read.today}
                refusal={writes.listRefusal}
                onOpenDay={writes.openException}
                onAddException={() => {
                  writes.openException(read.today);
                }}
                onAddBlock={writes.openBlock}
                onUndo={writes.ask}
                undoing={writes.asking}
              />
            </div>
          </>
        )}
      </div>

      <ExceptionDialog
        draft={writes.exception}
        usual={writes.exceptionUsual}
        today={read.today}
        saving={writes.saving}
        refusal={writes.dialogRefusal}
        onEdit={writes.editException}
        onMoveDate={writes.openException}
        onSave={writes.saveException}
        onClose={writes.closeDialog}
      />
      <BlockDialog
        draft={writes.block}
        nowMs={read.nowMs}
        zone={read.zone}
        saving={writes.saving}
        refusal={writes.dialogRefusal}
        onEdit={writes.editBlock}
        onSave={writes.saveBlock}
        onClose={writes.closeDialog}
      />
      {copyFrom === null ? null : (
        <CopyDayDialog
          from={copyFrom}
          onCopy={(onto) => {
            week.edit((pattern) => dayCopied(pattern, copyFrom, onto));
            setCopyFrom(null);
          }}
          onClose={() => {
            setCopyFrom(null);
          }}
        />
      )}
      <ConfirmDialog
        prompt={prompt}
        onConfirm={() => {
          if (week.leavePrompt !== null) week.leave();
          else if (askingMonday) {
            week.edit(mondayCopiedToWeek);
            setAskingMonday(false);
          } else writes.confirm();
        }}
        onCancel={() => {
          if (week.leavePrompt !== null) week.stay();
          else if (askingMonday) setAskingMonday(false);
          else writes.dismiss();
        }}
      />
      <LiveRegionAnnouncer message={announcer.message} />
    </div>
  );
}
